/* ============================================================
   pillar-flowchart.js
   Lucius Atelier · Pillar page interactive flowchart

   Responsibilities:
   1. Mount the rendered flowchart into the article body at the
      placeholder div (.la-pillar-flowchart-mount[data-flowchart=...])
   2. Bind hover events on desktop, tap events on mobile
   3. Position and populate the mini-card based on hovered/tapped node
   4. Handle close behavior (escape key, click outside, close button)
   5. Respect prefers-reduced-motion

   No dependencies — vanilla ES2018+. Uses defer in the <script> tag, so
   DOM is parsed before execution.
   ============================================================ */

(function () {
  'use strict';

  const MOBILE_BREAKPOINT = 768;
  const HOVER_DELAY_MS = 80;
  const HIDE_DELAY_MS = 150;

  // === Mount: move the flowchart from the section host into the article ====

  function mountFlowcharts() {
    const sources = document.querySelectorAll('.la-pillar-flowchart-source');
    if (!sources.length) return;

    sources.forEach(source => {
      const flowchartId = source.getAttribute('data-flowchart-id');
      if (!flowchartId) return;

      const target = document.querySelector(
        `.la-pillar-flowchart-mount[data-flowchart="${flowchartId}"]`
      );

      // If no mount point in article body, render in place at section location
      if (!target) {
        source.removeAttribute('hidden');
        initFlowchart(source);
        return;
      }

      // Move source content into target, then remove the now-empty source
      while (source.firstChild) {
        target.appendChild(source.firstChild);
      }
      source.parentNode.removeChild(source);

      // Activate
      initFlowchart(target);
    });
  }

  // === Per-flowchart initialization ========================================

  function initFlowchart(root) {
    const figure = root.querySelector('.la-flowchart');
    if (!figure) return;

    const card = figure.querySelector('.la-flowchart__card');
    const cardBody = figure.querySelector('.la-flowchart__card-body');
    const cardClose = figure.querySelector('.la-flowchart__card-close');
    const viewport = figure.querySelector('.la-flowchart__viewport');
    const nodes = figure.querySelectorAll('.la-node');
    const cards = figure.querySelectorAll('.la-flowchart__cards template');

    if (!card || !cardBody || !nodes.length) return;

    // Build a lookup map: node-id → template content
    const cardMap = new Map();
    cards.forEach(template => {
      const id = template.getAttribute('data-node-id');
      if (id) cardMap.set(id, template.innerHTML);
    });

    let hoverTimer = null;
    let hideTimer = null;
    let activeNode = null;

    function isMobile() {
      return window.innerWidth < MOBILE_BREAKPOINT;
    }

    function showCard(node) {
      const nodeId = node.getAttribute('data-node-id');
      if (!nodeId) return;

      const content = cardMap.get(nodeId);
      if (!content) return;

      cardBody.innerHTML = content;

      // Apply mobile class if on mobile (shows close button, switches positioning)
      if (isMobile()) {
        card.classList.add('is-mobile');
        // Clear any leftover desktop inline positioning so mobile fixed CSS wins
        card.style.left = '';
        card.style.top = '';
      } else {
        card.classList.remove('is-mobile');
        positionCardForNode(node);
      }

      // Update active state on nodes
      nodes.forEach(n => n.classList.remove('is-active'));
      node.classList.add('is-active');
      activeNode = node;

      card.classList.add('is-visible');
      card.setAttribute('aria-hidden', 'false');
    }

    function hideCard() {
      card.classList.remove('is-visible');
      card.setAttribute('aria-hidden', 'true');
      nodes.forEach(n => n.classList.remove('is-active'));
      activeNode = null;
    }

    function positionCardForNode(node) {
      const nodeId = node.getAttribute('data-node-id');
      const nodeRect = node.getBoundingClientRect();
      const viewportRect = viewport.getBoundingClientRect();

      const cardWidth = 360;
      const cardGap = 16;
      const padding = 16;

      // Read the card's actual rendered height. The card already has content
      // injected by showCard() at this point, so getBoundingClientRect gives
      // us the real measurement. We temporarily make it visible-but-offscreen
      // to get an accurate measurement even before the visibility transition.
      const prevTop = card.style.top;
      const prevLeft = card.style.left;
      const prevVis = card.style.visibility;
      card.style.visibility = 'hidden';
      card.style.left = '-9999px';
      card.style.top = '0px';
      const cardHeight = card.getBoundingClientRect().height || 320;
      card.style.visibility = prevVis;
      // (left/top will be overwritten below)

      const nodeCenterX = (nodeRect.left + nodeRect.width / 2) - viewportRect.left;
      const nodeCenterY = (nodeRect.top + nodeRect.height / 2) - viewportRect.top;

      let left, top;

      // Per-node positioning overrides — for nodes that need specific anchor behaviour
      // that the smart-default logic doesn't handle well.
      switch (nodeId) {
        case 'root':
          // Root sits at the top center; card flows BELOW the node
          left = Math.max(padding, nodeCenterX - cardWidth / 2);
          left = Math.min(left, viewportRect.width - cardWidth - padding);
          top = nodeRect.bottom - viewportRect.top + cardGap;
          break;

        case 'converge':
          // Convergence sits middle-bottom; card flows to the RIGHT, vertically aligned with node
          left = nodeRect.right - viewportRect.left + cardGap;
          if (left + cardWidth > viewportRect.width - padding) {
            // Overflow — fall back to LEFT side
            left = nodeRect.left - viewportRect.left - cardWidth - cardGap;
          }
          top = nodeRect.top - viewportRect.top;
          break;

        case 'diy':
          // DIY endpoint (left side, near bottom) — card flows RIGHT, bottom-aligned with node
          left = nodeRect.right - viewportRect.left + cardGap;
          if (left + cardWidth > viewportRect.width - padding) {
            // Fallback if no room to the right
            left = Math.max(padding, nodeCenterX - cardWidth / 2);
          }
          // Bottom-align the card with the node bottom
          top = nodeRect.bottom - viewportRect.top - cardHeight;
          if (top < padding) top = padding;
          break;

        case 'assembled':
          // Assembled endpoint (right side, near bottom) — card flows LEFT, bottom-aligned with node
          left = nodeRect.left - viewportRect.left - cardWidth - cardGap;
          if (left < padding) {
            // Fallback if no room to the left
            left = Math.max(padding, nodeCenterX - cardWidth / 2);
          }
          // Bottom-align the card with the node bottom
          top = nodeRect.bottom - viewportRect.top - cardHeight;
          if (top < padding) top = padding;
          break;

        default: {
          // Smart default for everything else: flow to the side opposite the node's location,
          // with vertical adjustment if the node sits near the bottom of the viewport.
          const nodeIsRightHalf = nodeCenterX > viewportRect.width * 0.5;
          const nodeIsBottomThird = nodeCenterY > viewportRect.height * 0.66;

          if (nodeIsRightHalf) {
            left = nodeRect.right - viewportRect.left - cardWidth - cardGap - nodeRect.width;
            // Recompute: prefer LEFT side of node
            left = nodeRect.left - viewportRect.left - cardWidth - cardGap;
            if (left < padding) {
              left = Math.max(padding, nodeCenterX - cardWidth / 2);
            }
          } else {
            left = nodeRect.right - viewportRect.left + cardGap;
            if (left + cardWidth > viewportRect.width - padding) {
              left = Math.min(viewportRect.width - cardWidth - padding, nodeCenterX - cardWidth / 2);
              left = Math.max(padding, left);
            }
          }

          if (nodeIsBottomThird) {
            // Bottom-align card with node bottom — like diy/assembled
            top = nodeRect.bottom - viewportRect.top - cardHeight;
            if (top < padding) top = padding;
          } else {
            top = nodeRect.top - viewportRect.top;
            if (top + cardHeight > viewportRect.height - padding) {
              top = Math.max(padding, viewportRect.height - cardHeight - padding);
            }
          }
          break;
        }
      }

      card.style.left = left + 'px';
      card.style.top = top + 'px';
    }

    // === Desktop: hover behavior ===========================================

    function bindHoverEvents() {
      nodes.forEach(node => {
        node.addEventListener('mouseenter', () => {
          if (isMobile()) return;
          clearTimeout(hideTimer);
          hoverTimer = setTimeout(() => showCard(node), HOVER_DELAY_MS);
        });

        node.addEventListener('mouseleave', () => {
          if (isMobile()) return;
          clearTimeout(hoverTimer);
          hideTimer = setTimeout(hideCard, HIDE_DELAY_MS);
        });
      });

      // Card itself should NOT trigger hide when hovered
      card.addEventListener('mouseenter', () => {
        if (isMobile()) return;
        clearTimeout(hideTimer);
      });

      card.addEventListener('mouseleave', () => {
        if (isMobile()) return;
        hideTimer = setTimeout(hideCard, HIDE_DELAY_MS);
      });
    }

    // === Mobile / touch: tap behavior ======================================

    function bindTapEvents() {
      nodes.forEach(node => {
        node.addEventListener('click', e => {
          if (!isMobile()) return; // Desktop click is handled by keyboard/focus only
          e.preventDefault();
          e.stopPropagation();

          if (activeNode === node) {
            hideCard();
          } else {
            showCard(node);
          }
        });
      });

      // Tap outside card to close
      document.addEventListener('click', e => {
        if (!isMobile()) return;
        if (card.contains(e.target)) return;
        if ([...nodes].some(n => n.contains(e.target))) return;
        hideCard();
      });
    }

    // === Keyboard: focus + Enter/Space + Escape ============================

    function bindKeyboardEvents() {
      nodes.forEach(node => {
        node.addEventListener('keydown', e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (activeNode === node) {
              hideCard();
            } else {
              showCard(node);
            }
          }
        });

        node.addEventListener('focus', () => {
          if (!isMobile()) showCard(node);
        });

        node.addEventListener('blur', e => {
          // Don't hide if focus moved into the card
          if (!isMobile() && card.contains(e.relatedTarget)) return;
          if (!isMobile()) hideCard();
        });
      });

      document.addEventListener('keydown', e => {
        if (e.key === 'Escape' && activeNode) {
          hideCard();
          activeNode && activeNode.focus();
        }
      });
    }

    // === Close button (mobile) =============================================

    if (cardClose) {
      cardClose.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        hideCard();
      });
    }

    // === Reposition card on resize / scroll ================================

    let resizeTimer = null;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        if (activeNode && !isMobile()) {
          positionCardForNode(activeNode);
        } else if (activeNode && isMobile()) {
          card.classList.add('is-mobile');
        }
      }, 100);
    });

    // === Wire it all up ====================================================

    bindHoverEvents();
    bindTapEvents();
    bindKeyboardEvents();
  }

  // === Boot ================================================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountFlowcharts);
  } else {
    mountFlowcharts();
  }
})();
